import './monitoring/init';
import React, { useEffect } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import App from './App';
import './index.css';
import { seoForPath, OG_IMAGE } from './i18n/seoMeta';
import { MARKETING_ROUTES } from './marketing/routes';
import BrowserErrorBoundary from './monitoring/BrowserErrorBoundary';
import { reportRenderFailure } from './monitoring/browserErrors';

const PRERENDERED_PATHS = new Set([
  '/',
  '/zh',
  '/zh/',
  '/privacy',
  '/privacy/',
  ...MARKETING_ROUTES.flatMap((item) => [item.path, item.path.replace(/\/$/, '')]),
]);

// On the client `window` exists → BrowserRouter; during build-time prerender
// there is no window → StaticRouter driven by the route's `url`.
function Root({ url }: { url?: string }) {
  useEffect(() => {
    // The first React route is now committed; reveal its loader/auth/workspace.
    document.documentElement.removeAttribute('data-radar-boot');
  }, []);
  return (
    <React.StrictMode>
      {typeof window === 'undefined' ? (
        <StaticRouter location={url ?? '/'}>
          <BrowserErrorBoundary><App /></BrowserErrorBoundary>
        </StaticRouter>
      ) : (
        <BrowserRouter>
          <BrowserErrorBoundary><App /></BrowserErrorBoundary>
        </BrowserRouter>
      )}
    </React.StrictMode>
  );
}

if (typeof window !== 'undefined') {
  const target = document.getElementById('root')!;
  // Hydrate only when the server HTML for THIS path was actually prerendered.
  // Dev (empty shell) and SPA-fallback routes (/beta, /onboarding, …) get a
  // fresh createRoot to avoid a hydration mismatch + homepage flash.
  if (import.meta.env.DEV || !PRERENDERED_PATHS.has(window.location.pathname)) {
    createRoot(target).render(<Root />);
  } else {
    hydrateRoot(target, <Root />, { onRecoverableError: error => reportRenderFailure(error, 'hydration') });
  }
}

export async function prerender(data: { url: string }) {
  const { renderToString } = await import('react-dom/server');

  const html = renderToString(<Root url={data.url} />);

  const seo = seoForPath(data.url);

  const elements = new Set([
    { type: 'meta', props: { name: 'description', content: seo.description } },
    { type: 'meta', props: { name: 'robots', content: seo.robots ?? 'index, follow' } },
    { type: 'meta', props: { property: 'og:type', content: seo.jsonLd?.['@type'] === 'Article' ? 'article' : 'website' } },
    { type: 'link', props: { rel: 'canonical', href: seo.canonical } },
    { type: 'meta', props: { property: 'og:title', content: seo.title } },
    { type: 'meta', props: { property: 'og:description', content: seo.description } },
    { type: 'meta', props: { property: 'og:url', content: seo.canonical } },
    { type: 'meta', props: { property: 'og:locale', content: seo.ogLocale } },
    { type: 'meta', props: { property: 'og:image', content: seo.image ?? OG_IMAGE } },
    { type: 'meta', props: { name: 'twitter:image', content: seo.image ?? OG_IMAGE } },
    { type: 'meta', props: { property: 'og:image:alt', content: seo.imageAlt } },
    { type: 'meta', props: { name: 'twitter:title', content: seo.title } },
    { type: 'meta', props: { name: 'twitter:description', content: seo.description } },
  ]);

  return {
    html,
    head: {
      lang: seo.htmlLang,
      title: seo.title,
      elements,
    },
  };
}
